import React, { useRef, useCallback, useEffect, useContext } from 'react'
import s from './ResponsiveScrollableWrapper.less'
import { isGE } from '../../Device'

export const ScrollContext = React.createContext()

export const useScrollContext = () => useContext(ScrollContext)

const TOP = 0

const ResponsiveScrollableWrapper = ({ children }) => {
  const scrollableElementRef = useRef()

  const getScrollValue = useCallback(() => {
    return isGE() ? scrollableElementRef?.current?.scrollTop ?? TOP : document.querySelector('html').scrollTop
  }, [])

  const setScrollValue = useCallback(value => {
    if (isGE()) {
      if (scrollableElementRef?.current) {
        scrollableElementRef.current.scrollTop = value
      }
    } else {
      document.querySelector('html').scrollTop = value
      document.querySelector('body').scrollTop = value
    }
  }, [])

  const scrollTo = useCallback((scrollGoal, animate = false) => {
    if (!animate) return setScrollValue(scrollGoal)

    let now0 = null
    const scroll0 = getScrollValue()
    const scrollDelta = scrollGoal - scroll0
    const duration = 500
    const step = now => {
      if (now0 === null) now0 = now
      const t = now - now0
      const scrollPos = scroll0 + scrollDelta * Math.sin(((Math.PI / 2) * t) / duration)
      if (t < duration) {
        setScrollValue(scrollPos)
        requestAnimationFrame(step)
      } else {
        setScrollValue(scrollGoal)
      }
    }
    requestAnimationFrame(step)
  }, [])

  const scrollTop = useCallback(
    animate => {
      scrollTo(TOP, animate)
    },
    [scrollTo]
  )

  useEffect(() => {
    scrollTop()
    return () => scrollTop()
  }, [])

  const addScrollListener = useCallback(fn => {
    const el = isGE() ? scrollableElementRef?.current : document
    if (el) el.addEventListener('scroll', fn)
  }, [])

  const removeScrollListener = useCallback(fn => {
    const el = isGE() ? scrollableElementRef?.current : document
    if (el) el.removeEventListener('scroll', fn)
  }, [])

  const checkIfTargetIsVisible = useCallback((parent, child) => {
    if (!parent || !child) return false

    const targetTop = child.offsetTop
    const totalScrollHeight = isGE() ? parent.offsetParent.clientHeight : window.innerHeight
    const scrollValue = getScrollValue()

    const alreadyVisible = targetTop < totalScrollHeight
    const scrolledUntilTarget = scrollValue > targetTop - totalScrollHeight
    return alreadyVisible || scrolledUntilTarget
  }, [])

  const providerValue = {
    scrollTop,
    scrollTo,
    getScrollValue,
    addScrollListener,
    removeScrollListener,
    checkIfTargetIsVisible
  }

  return (
    <div ref={scrollableElementRef} className={s.responsiveScrollableWrapper}>
      <ScrollContext.Provider value={providerValue}>{children}</ScrollContext.Provider>
    </div>
  )
}

export default ResponsiveScrollableWrapper
